export type DisplayHolidayWeekday = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";

export type DisplayHolidayDate = {
  date: string;
  memo: string;
};

export type DisplayHolidaySettings = {
  weekly: DisplayHolidayWeekday[];
  dates: DisplayHolidayDate[];
};

type DisplayHolidaySource = {
  weekly?: unknown;
  dates?: unknown;
} | null | undefined;

export const DISPLAY_HOLIDAY_WEEKDAY_OPTIONS: Array<{
  value: DisplayHolidayWeekday;
  label: string;
}> = [
  { value: "sun", label: "일" },
  { value: "mon", label: "월" },
  { value: "tue", label: "화" },
  { value: "wed", label: "수" },
  { value: "thu", label: "목" },
  { value: "fri", label: "금" },
  { value: "sat", label: "토" },
];

const DISPLAY_HOLIDAY_WEEKDAYS = new Set<DisplayHolidayWeekday>(
  DISPLAY_HOLIDAY_WEEKDAY_OPTIONS.map((option) => option.value),
);

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const isDisplayHolidayWeekday = (value: unknown): value is DisplayHolidayWeekday => {
  return typeof value === "string" && DISPLAY_HOLIDAY_WEEKDAYS.has(value as DisplayHolidayWeekday);
};

const isValidDateOnly = (value: unknown): value is string => {
  if (typeof value !== "string") {
    return false;
  }

  const matched = value.match(DATE_ONLY_PATTERN);
  if (!matched) {
    return false;
  }

  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);
  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  );
};

export const normalizeDisplayHolidaySettings = (
  source: DisplayHolidaySource,
): DisplayHolidaySettings => {
  const weekly: DisplayHolidayWeekday[] = [];
  const seenWeekdays = new Set<DisplayHolidayWeekday>();

  if (Array.isArray(source?.weekly)) {
    source.weekly.forEach((weekday) => {
      if (!isDisplayHolidayWeekday(weekday) || seenWeekdays.has(weekday)) {
        return;
      }

      seenWeekdays.add(weekday);
      weekly.push(weekday);
    });
  }

  const dates: DisplayHolidayDate[] = [];
  const seenDates = new Set<string>();

  if (Array.isArray(source?.dates)) {
    source.dates.forEach((entry) => {
      if (!entry || typeof entry !== "object" || !("date" in entry)) {
        return;
      }

      const date = (entry as { date?: unknown }).date;
      if (!isValidDateOnly(date) || seenDates.has(date)) {
        return;
      }

      seenDates.add(date);
      dates.push({
        date,
        memo: String((entry as { memo?: unknown }).memo ?? "").trim(),
      });
    });
  }

  return { weekly, dates };
};

export const buildDisplayHolidaysInfoPayload = (settings: DisplayHolidaySettings) => {
  return {
    display_holidays: normalizeDisplayHolidaySettings(settings),
  };
};
